/**
 * negotiationBrief.js — API client for the negotiation-brief extractor
 * (Lane 763, Phase 1b).
 *
 * ONE call. `POST /api/chat/brief/evaluate` is fired after a successful
 * assistant reply, exactly as chat capture's evaluate is, and for the same
 * Rule 108 reason: extraction must never sit inside /api/ai-assistant's
 * latency budget.
 *
 * WHY THIS IS A SEPARATE CALL FROM chatCapture.evaluateMessage AND NOT ONE
 * MORE FIELD ON IT. They ask different questions of the same turn — capture
 * asks "is this a fact about a supplier?", the brief asks "does this change
 * how we negotiate with them?" — and, more concretely, the backend cannot
 * serve both in one request: /api/chat/capture/evaluate holds a pooled
 * connection across its own Haiku call, and the live pool is three
 * connections per worker. Chaining a second 15s model call inside it would
 * hold one of those three for up to 30 seconds of every chat turn.
 *
 * ⚠️ THIS FUNCTION NEVER THROWS. It DOES now return something the UI uses --
 * Phase 1c (Lane 773) is the lane the old form of this note said would change
 * it, and this is that change. On `status === 'captured'` the body carries a
 * `disclosure` object and `Home.js` renders its `text` beneath the message that
 * caused the write, with an Undo wired to `retractBriefEntry`.
 *
 * WHAT DID NOT CHANGE, AND MUST NOT. The call is still fire-and-forget: the
 * caller does not `await` it, so it still cannot enter /api/ai-assistant's
 * latency budget (Rule 108). A failure is still invisible to someone
 * mid-conversation -- a rejected fetch resolves to `null` after a console line
 * rather than propagating -- and every non-`captured` status still carries no
 * `disclosure` key at all, so a turn that stored nothing renders nothing.
 */

import { API_BASE } from '../config';


/**
 * Fire brief extraction for one completed chat turn.
 *
 * @param {Object}  args
 * @param {string}  args.rawText            the buyer's message, VERBATIM.
 *   ⚠️ Never trim, split or "clean" this. D61 (locked 2026-08-03): a
 *   qualifier, retraction or hedge must never be separated from the claim it
 *   qualifies. The backend's own pre-gate has no discard path for exactly
 *   this reason, and that guarantee is worthless if the client pre-chews the
 *   text.
 * @param {?number} args.supplierId         from `data.brief_scope.supplier_id`
 *   on the /api/ai-assistant response. Absent on any turn that was not about
 *   one supplier; the endpoint then skips without paying for a model call.
 *   Re-validated server-side against the caller's company — this is scoping,
 *   not authorisation.
 * @param {?string} args.assistantResponse  the reply just rendered. It is
 *   where the meaning is often made explicit ("You lose the delivery
 *   performance trade lever"), so it is worth sending even though it can
 *   never be quoted as the buyer's words.
 * @param {?string} args.conversationId
 * @param {?number} args.chatMessageId
 * @returns {Promise<Object|null>} the status body, or null on any failure.
 *   On `status === 'captured'` it also carries
 *   `disclosure: {supplier_id, supplier_name, assertion_ids, text} | null` --
 *   the line the chat says back, and the ids its Undo retracts. `null` when the
 *   write could not be described honestly; the key is absent on every other
 *   status.
 */
export async function evaluateBrief({
  rawText,
  supplierId = null,
  assistantResponse = null,
  conversationId = null,
  chatMessageId = null,
  clientTurnId = null,
  derivedFactResponse = false,
} = {}) {
  const token = localStorage.getItem('authToken');
  const body = {
    raw_text: rawText,
    supplier_id: supplierId,
    assistant_response: assistantResponse,
    conversation_id: conversationId,
    chat_message_id: chatMessageId,
    client_turn_id: clientTurnId,
    derived_fact_response: derivedFactResponse,
  };

  try {
    const res = await fetch(`${API_BASE}/api/chat/brief/evaluate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      console.log('[BRIEF-EXTRACT] non-ok', { status: res.status });
      return null;
    }
    const text = await res.text();
    return text ? JSON.parse(text) : null;
  } catch (err) {
    console.log('[BRIEF-EXTRACT] request failed', err?.message || err);
    return null;
  }
}


/* =========================================================================
 * PHASE 4 (Lane 766) -- THE AUDIT / EDIT SURFACE
 *
 * Ryan: "if they do go straight to the deck builder, then there needs to be a
 * way to audit it and edit it in there too."
 *
 * ⚠️ THESE DO NOT SHARE `evaluateBrief`'s SWALLOW-EVERYTHING CONTRACT, AND THE
 * DIFFERENCE IS DELIBERATE. `evaluateBrief` is fire-and-forget after a chat
 * turn, so a failure must be invisible to someone mid-conversation. These are
 * called by a buyer who just clicked "rule this out" and is watching -- telling
 * them it worked when it did not is the same silent-disagreement failure the
 * whole programme exists to fix. So these THROW, and the panel shows the error.
 *
 * The GET is the one exception: a 404 is the module-dark shape (flag off, or a
 * supplier this tenant does not own) and resolves to `null` so the panel renders
 * NOTHING rather than an error. With the flag off the deck builder is
 * byte-identical to before this lane.
 *
 * Every MUTATION returns the whole refreshed payload, and the caller replaces
 * its state with it wholesale rather than patching locally (Rule 111): the
 * thing being displayed is which arguments the deck will make, and that is
 * precisely what the buyer opened the panel to stop guessing about.
 * ========================================================================= */

const BRIEF_AUDIT_PATH = '/api/decks/negotiation-brief';

function authHeadersFrom(token) {
  const t = token || localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(t ? { Authorization: `Bearer ${t}` } : {}),
  };
}

async function briefPost(path, body, { apiBase = API_BASE, token = null } = {}) {
  const res = await fetch(`${apiBase}${path}`, {
    method: 'POST',
    headers: authHeadersFrom(token),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const detail = typeof data.detail === 'object'
      ? (data.detail && data.detail.message) : data.detail;
    throw new Error(detail || `That did not save (${res.status}).`);
  }
  return res.json();
}

/**
 * Read what this deck is going to argue, before it argues it.
 *
 * @returns {Promise<Object|null>} the audit payload, or `null` when the surface
 *   is dark for this tenant / this supplier is not ours (both are a 404, and
 *   they are indistinguishable on purpose -- Rule 108, no existence enumeration
 *   across tenants).
 */
export async function fetchBriefAudit(supplierId,
  { apiBase = API_BASE, token = null } = {}) {
  if (!supplierId) return null;
  const res = await fetch(
    `${apiBase}${BRIEF_AUDIT_PATH}?supplier_id=${encodeURIComponent(supplierId)}`,
    { headers: authHeadersFrom(token) },
  );
  if (res.status === 404) return null;   // dark, or not ours. Render nothing.
  if (!res.ok) throw new Error(`Could not load the brief (${res.status}).`);
  return res.json();
}

/**
 * Rule one argument out of the deck, or promote it to lead.
 *
 * ⚠️ `reason` is stored as the buyer's own words and is quoted back to them --
 * and to the chat, which renders it as `His words: "..."`. The panel shows them
 * that exact sentence in an editable field before this is called. Never call
 * this with text the buyer has not seen. `presetKey` is a POINTER to which
 * canned sentence was offered; the SERVER decides whether it was chosen, edited
 * or typed by comparing against its own copy.
 */
export function setBriefLever({ supplierId, leverKey, action, reason,
  presetKey = null }, opts = {}) {
  return briefPost(`${BRIEF_AUDIT_PATH}/lever`, {
    supplier_id: supplierId,
    lever_key: leverKey,
    action,
    reason,
    preset_key: presetKey,
  }, opts);
}

/**
 * Set how hard we can push this supplier at all.
 *
 * ⚠️ INTERNAL ONLY, ALWAYS. Nothing written here may ever reach a supplier --
 * downstream it becomes tone rules ("write asks as requests"), never a sentence.
 */
export function setBriefLeverage({ supplierId, posture, reason,
  presetKey = null }, opts = {}) {
  return briefPost(`${BRIEF_AUDIT_PATH}/leverage`, {
    supplier_id: supplierId,
    posture,
    reason,
    preset_key: presetKey,
  }, opts);
}

/**
 * Approve the EXACT sentence a floated offer becomes for the supplier.
 *
 * ⚠️ THE ONE PLACE ANYTHING IN THE BRIEF CROSSES THE AUDIENCE BOUNDARY. A
 * floated offer is the buyer musing ("maybe they could route through our
 * Singapore location"); rendering that hedge as a proposal would put a
 * commitment in their mouth they never made. `approvedText` must be the literal
 * sentence, not a summary of it.
 *
 * ⚠️ AND IT MUST NOT BE WHAT THEY FLOATED (Lane 769). The store REFUSES an
 * `approvedText` that normalises equal to the floated words, or to the
 * extractor's summary of them, with a 400 that `briefPost` turns into a message
 * the panel shows. That refusal is the real guard; the panel's disabled button
 * is the courtesy that means a buyer rarely meets it. Nothing is written on a
 * refusal and the floated offer stays live, so the fix is to edit and save again.
 *
 * `offerSeed` is a POINTER to what the panel put in the box -- 'floated' when it
 * pre-filled their own words, null when they started blank. The server decides
 * the stored provenance and treats an absent seed as the weaker claim.
 */
export function promoteBriefOffer({ supplierId, floatedAssertionId,
  approvedText, offerSeed = null }, opts = {}) {
  return briefPost(`${BRIEF_AUDIT_PATH}/offer`, {
    supplier_id: supplierId,
    floated_assertion_id: floatedAssertionId,
    approved_text: approvedText,
    offer_seed: offerSeed,
  }, opts);
}

/**
 * Undo one brief entry. Soft-delete -- the record is never removed.
 *
 * ⚠️ EXACTLY ONE of `leverKey` / `assertionId`, and they are different
 * operations. `leverKey` clears every live row for that named lever (correct:
 * un-killing "delivery" must un-kill delivery, not the most recent time they
 * said it). `assertionId` clears one row, and it is the ONLY path for a kill
 * the extractor could not map to a lever -- the store REFUSES to retract those
 * by key, because sweeping them together would undo all of them at once.
 */
export function retractBriefEntry({ supplierId, leverKey = null,
  assertionId = null, predicate = null }, opts = {}) {
  return briefPost(`${BRIEF_AUDIT_PATH}/retract`, {
    supplier_id: supplierId,
    lever_key: leverKey,
    assertion_id: assertionId,
    predicate,
  }, opts);
}
